import { useCallback, useEffect, useState } from 'react';
import { getSSToken } from '../../../utils/token';
import { AvatarDetail, Feature, FeatureCollection, FilterState, PaginationInfo } from '../types';
import { FILTER_STATUS, MAP_CONFIG, THRESHOLD_COLORS } from '../constants';
import { getANCNextMaintenanceOrEmptyingFromQuestion190, ANCNextMaintenanceOrEmptying } from '../../../utils/metabase/metabaseService';
import { postalCodeInDepartments } from '../../prestataires/departments';

/**
 * Gets the priority level for a threshold name by extracting the numeric part
 * Example: "threshold.level.3" -> 3, "threshold.level.1" -> 1
 * Returns 0 for null/undefined/invalid values
 */
const getThresholdLevel = (thresholdName: string | null | undefined): number => {
    if (!thresholdName) return 0;

    // Split by "." and get the last part which should be the numeric level
    const parts = thresholdName.split('.');
    const lastPart = parts[parts.length - 1];

    // Convert to number
    const level = parseInt(lastPart, 10);

    // Return the level if it's a valid number, otherwise return 0
    return isNaN(level) ? 0 : level;
};

/**
 * Determines the maximum threshold from maintenance and emptying thresholds
 * Returns the threshold name with the highest priority
 */
const getMaxThreshold = (
    maintenanceThreshold: string | null | undefined,
    emptyingThreshold: string | null | undefined,
): string | null => {
    const maintenanceLevel = getThresholdLevel(maintenanceThreshold);
    const emptyingLevel = getThresholdLevel(emptyingThreshold);

    if (maintenanceLevel === 0 && emptyingLevel === 0) {
        return null;
    }

    if (maintenanceLevel >= emptyingLevel) {
        return maintenanceThreshold || null;
    } else {
        return emptyingThreshold || null;
    }
};

/**
 * Gets the color for a given threshold
 */
const getThresholdColor = (threshold: string | null | undefined): string => {
    if (!threshold) return THRESHOLD_COLORS.default;
    return THRESHOLD_COLORS[threshold as keyof typeof THRESHOLD_COLORS] || THRESHOLD_COLORS.default;
};

/**
 * Merges metadata from question 190 into feature properties based on avatarId
 */
const mergeMetadata = (features: Feature[], metadata: ANCNextMaintenanceOrEmptying[]): Feature[] => {
    // Create a map of avatar_id to metadata for efficient lookup
    const metadataMap = new Map<string, ANCNextMaintenanceOrEmptying[]>();
    
    metadata.forEach(item => {
        if (item.avatar_id) {
            if (!metadataMap.has(item.avatar_id)) {
                metadataMap.set(item.avatar_id, []);
            }
            metadataMap.get(item.avatar_id)!.push(item);
        }
    });

    // Merge metadata into features
    return features.map(feature => {
        const avatarId = feature.properties.avatarId;
        const avatarMetadata = metadataMap.get(avatarId) || [];
        
        // Get next maintenance and vidange
        const nextMaintenance = getNextMaintenance(avatarMetadata);
        const nextVidange = getNextVidange(avatarMetadata);
        
        const updatedFeature = {
            ...feature,
            properties: {
                ...feature.properties,
                // Add the metadata from question 190
                metabaseMetadata: avatarMetadata,
                hasMaintenanceData: avatarMetadata.length > 0,
                maintenanceCount: avatarMetadata.length,
                // Add next maintenance and vidange information
                nextMaintenance: nextMaintenance,
                nextVidange: nextVidange,
                hasNextMaintenance: nextMaintenance !== null,
                hasNextVidange: nextVidange !== null,
            }
        };
        
        return updatedFeature;
    });
};

/**
 * Checks if a feature has maintenance scheduled within the specified number of months
 */
const hasMaintenanceWithinMonths = (feature: Feature, months: number): boolean => {
    const metadata = feature.properties.metabaseMetadata as ANCNextMaintenanceOrEmptying[];
    
    if (!metadata || metadata.length === 0) {
        return false;
    }

    const today = new Date();
    const futureDate = new Date();
    futureDate.setMonth(today.getMonth() + months);

    return metadata.some(item => {
        if (!item.execution_date) return false;
        
        const executionDate = new Date(item.execution_date);
        return executionDate >= today && executionDate <= futureDate && item.threshold_type === 'maintenance';
    });
};

/**
 * Checks if a feature has vidange (emptying) scheduled within the specified number of months
 */
const hasVidangeWithinMonths = (feature: Feature, months: number): boolean => {
    const metadata = feature.properties.metabaseMetadata as ANCNextMaintenanceOrEmptying[];
    
    if (!metadata || metadata.length === 0) {
        return false;
    }

    const today = new Date();
    const futureDate = new Date();
    futureDate.setMonth(today.getMonth() + months);

    return metadata.some(item => {
        if (!item.execution_date) return false;
        
        const executionDate = new Date(item.execution_date);
        return executionDate >= today && executionDate <= futureDate && item.threshold_type === 'emptying';
    });
};

/**
 * Gets the next maintenance from metadata
 */
const getNextMaintenance = (metadata: ANCNextMaintenanceOrEmptying[]): ANCNextMaintenanceOrEmptying | null => {
    const today = new Date();
    
    const maintenanceItems = metadata
        .filter(item => item.threshold_type === 'maintenance' && item.execution_date)
        .map(item => ({
            ...item,
            executionDate: new Date(item.execution_date)
        }))
        .filter(item => item.executionDate >= today)
        .sort((a, b) => a.executionDate.getTime() - b.executionDate.getTime());


    return maintenanceItems.length > 0 ? maintenanceItems[0] : null;
};

/**
 * Gets the next vidange from metadata
 */
const getNextVidange = (metadata: ANCNextMaintenanceOrEmptying[]): ANCNextMaintenanceOrEmptying | null => {
    const today = new Date();
    
    const vidangeItems = metadata
        .filter(item => item.threshold_type === 'emptying' && item.execution_date)
        .map(item => ({
            ...item,
            executionDate: new Date(item.execution_date)
        }))
        .filter(item => item.executionDate >= today)
        .sort((a, b) => a.executionDate.getTime() - b.executionDate.getTime());


    return vidangeItems.length > 0 ? vidangeItems[0] : null;
};

export const useMapData = (filters: FilterState, urlBase: string) => {
    const [data, setData] = useState<FeatureCollection>({ type: 'FeatureCollection', features: [] });
    const [allFeatures, setAllFeatures] = useState<Feature[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);

    const transformToFeature = useCallback((item: AvatarDetail): Feature => {
        const latitude = parseFloat(item.latitude) || 0;
        const longitude = parseFloat(item.longitude) || 0;

        // Debug: Check for missing avatarId
        if (!item.avatarId) {
            console.warn('Item missing avatarId:', item);
        }

        // Calculate the maximum threshold from both maintenance and emptying thresholds
        const maxThreshold = getMaxThreshold(
            item.productLastMaintenanceThresholdName,
            item.productLastEmptyingThresholdName,
        );
        const thresholdColor = getThresholdColor(maxThreshold);
        const thresholdLevel = getThresholdLevel(maxThreshold);

        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [longitude, latitude],
            },
            properties: {
                avatarId: item.avatarId,
                currentStateAvatarColor: item.productCurrentStatusName ? '#007bff' : '#666',
                thresholdColor: thresholdColor,
                maxThreshold: maxThreshold,
                thresholdLevel: thresholdLevel,
                criticality: 1,
                metadataAvatarTypeId: item.metadataAvatarTypeId,
                metadataAvatarType: {
                    id: item.metadataAvatarTypeId,
                    name: item.productTypeName,
                },
                typeCoreId: item.groupMetadataAvatarTypeId,
                avatarType: item.productTypology,
                type: 'AVATAR',
                productTypology: item.productTypology,
                productTypeName: item.productTypeName,
                productSerialNumber: item.productSerialNumber,
                productCode: item.productCode,
                productPostalCode: item.productPostalCode,
                productCurrentStatusName: item.productCurrentStatusName,
                productInstallationDate: item.productInstallationDate,
                productLastMaintenanceDate: item.productLastMaintenanceDate,
                productLastMaintenanceThresholdName: item.productLastMaintenanceThresholdName,
                productLastEmptyingThresholdName: item.productLastEmptyingThresholdName,
                productLastEmptyingDate: item.productLastEmptyingDate,
                addresstext: item.addresstext,
                description: item.description,
                comment: item.comment,
            },
        };
    }, []);

    // Client-side filtering function
    const applyFilters = useCallback((features: Feature[], filterState: FilterState): Feature[] => {
        return features.filter((feature) => {
            const props = feature.properties;

            // Filter by department
            if (filterState.departmentCodes.length && !postalCodeInDepartments(props.productPostalCode, filterState.departmentCodes)) {
                return false;
            }

            // Filter by product serial number
            if (filterState.productSerialNumber) {
                if (!props.productSerialNumber || !props.productSerialNumber.toLowerCase().includes(filterState.productSerialNumber.toLowerCase())) {
                    return false;
                }
            }

            // Filter by metadata avatar type ID
            if (filterState.metadataAvatarTypeId) {
                if (!props.metadataAvatarTypeId || props.metadataAvatarTypeId !== filterState.metadataAvatarTypeId) {
                    return false;
                }
            }

            // Filter by group metadata avatar type ID
            if (filterState.groupMetadataAvatarTypeId) {
                if (!props.typeCoreId || props.typeCoreId !== filterState.groupMetadataAvatarTypeId) {
                    return false;
                }
            }

            // Filter by installation date
            if (filterState.productInstallationDateAfter && props.productInstallationDate) {
                if (props.productInstallationDate < filterState.productInstallationDateAfter) {
                    return false;
                }
            }
            if (filterState.productInstallationDateBefore && props.productInstallationDate) {
                if (props.productInstallationDate > filterState.productInstallationDateBefore) {
                    return false;
                }
            }

            // Filter by last maintenance date
            if (filterState.productLastMaintenanceDateAfter && props.productLastMaintenanceDate) {
                if (props.productLastMaintenanceDate < filterState.productLastMaintenanceDateAfter) {
                    return false;
                }
            }
            if (filterState.productLastMaintenanceDateBefore && props.productLastMaintenanceDate) {
                if (props.productLastMaintenanceDate > filterState.productLastMaintenanceDateBefore) {
                    return false;
                }
            }

            // Filter by last emptying date
            if (filterState.productLastEmptyingDateAfter && props.productLastEmptyingDate) {
                if (props.productLastEmptyingDate < filterState.productLastEmptyingDateAfter) {
                    return false;
                }
            }
            if (filterState.productLastEmptyingDateBefore && props.productLastEmptyingDate) {
                if (props.productLastEmptyingDate > filterState.productLastEmptyingDateBefore) {
                    return false;
                }
            }

            // Filter by maintenance threshold name
            if (filterState.productLastMaintenanceThresholdName) {
                if (!props.productLastMaintenanceThresholdName || 
                    props.productLastMaintenanceThresholdName !== filterState.productLastMaintenanceThresholdName) {
                    return false;
                }
            }

            // Filter by emptying threshold name
            if (filterState.productLastEmptyingThresholdName) {
                if (!props.productLastEmptyingThresholdName || 
                    props.productLastEmptyingThresholdName !== filterState.productLastEmptyingThresholdName) {
                    return false;
                }
            }

            // Filter by maintenance in 3 months
            if (filterState.maintenanceIn3Months) {
                if (!hasMaintenanceWithinMonths(feature, 3)) {
                    return false;
                }
            }

            // Filter by maintenance in 6 months
            if (filterState.maintenanceIn6Months) {
                if (!hasMaintenanceWithinMonths(feature, 6)) {
                    return false;
                }
            }

            // Filter by maintenance in 12 months
            if (filterState.maintenanceIn12Months) {
                if (!hasMaintenanceWithinMonths(feature, 12)) {
                    return false;
                }
            }

            // Filter by vidange in 3 months
            if (filterState.vidangeIn3Months) {
                if (!hasVidangeWithinMonths(feature, 3)) {
                    return false;
                }
            }

            // Filter by vidange in 6 months
            if (filterState.vidangeIn6Months) {
                if (!hasVidangeWithinMonths(feature, 6)) {
                    return false;
                }
            }

            // Filter by vidange in 12 months
            if (filterState.vidangeIn12Months) {
                if (!hasVidangeWithinMonths(feature, 12)) {
                    return false;
                }
            }

            // Filter by hors maintenance (last maintenance at level 2 or 3)
            if (filterState.maintenanceHors) {
                const maintenanceThreshold = feature.properties.productLastMaintenanceThresholdName;
                const maintenanceLevel = getThresholdLevel(maintenanceThreshold);
                if (maintenanceLevel < 2) {
                    return false;
                }
            }

            // Filter by hors vidange (last vidange at level 2 or 3)
            if (filterState.vidangeHors) {
                const vidangeThreshold = feature.properties.productLastEmptyingThresholdName;
                const vidangeLevel = getThresholdLevel(vidangeThreshold);
                if (vidangeLevel < 2) {
                    return false;
                }
            }

            return true;
        });
    }, []);

    const fetchAllData = useCallback(async () => {
        setError(null);
        setLoading(true);
        setPaginationInfo(null);

        try {
            const token = await getSSToken();
            if (!token) {
                setError('No authentication token available');
                setLoading(false);
                return;
            }

            let allFeatures: Feature[] = [];
            let currentPage = 1;
            let itemsPerPage = MAP_CONFIG.DEFAULT_ITEMS_PER_PAGE;
            let totalItems = 0;
            let totalPages = 1;
            let rejectedItems = 0;
            let actualApiItems = 0;

            while (true) {
                const searchParams = new URLSearchParams();
                searchParams.set('page', String(currentPage));
                searchParams.set('itemsPerPage', String(itemsPerPage));

                searchParams.set('deleted', String(false));

                // Fixed status filters only - no user filters
                searchParams.set('productCurrentStatusName[0]', FILTER_STATUS[0]);
                searchParams.set('productCurrentStatusName[1]', FILTER_STATUS[1]);
                searchParams.set('productCurrentStatusName[2]', FILTER_STATUS[2]);
                searchParams.set('productCurrentStatusName[3]', FILTER_STATUS[3]);

                // searchParams.set('exists[latitude]', 'true');
                // searchParams.set('exists[longitude]', 'true');

                const requestUrl = `${urlBase}?${searchParams.toString()}`;
                const response = await fetch(requestUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`.trim(),
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const responseData = await response.json();
                const items: AvatarDetail[] = (responseData && responseData['hydra:member']) || [];
                
                // Count actual items received from API
                actualApiItems += items.length;

                if (currentPage === 1) {
                    totalItems =
                        typeof responseData['hydra:totalItems'] === 'number' ? responseData['hydra:totalItems'] : 0;
                    const apiItemsPerPage = Number(responseData['hydra:itemsPerPage']);
                    itemsPerPage = !isNaN(apiItemsPerPage) && apiItemsPerPage > 0 ? apiItemsPerPage : itemsPerPage;
                    totalPages =
                        totalItems > 0 ? Math.max(1, Math.ceil(totalItems / itemsPerPage)) : MAP_CONFIG.MAX_PAGES;
                }

                // Keep only valid coordinates
                const validItems = items.filter((item) => {
                    const lat = parseFloat(item.latitude);
                    const lng = parseFloat(item.longitude);
                    const valid = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 && isFinite(lat) && isFinite(lng);

                    if (!valid) {
                        console.log('Invalid item:', item);
                    }
                    
                    return valid;
                });

                // Count rejected items for this page
                const pageRejectedItems = items.length - validItems.length;
                rejectedItems += pageRejectedItems;

                const features: Feature[] = validItems.map(transformToFeature);
                allFeatures = [...allFeatures, ...features];
                
                const requestsCompleted = currentPage;
                const requestsTotal =
                    totalItems > 0
                        ? Math.max(1, Math.ceil(totalItems / itemsPerPage))
                        : Math.min(MAP_CONFIG.MAX_PAGES, totalPages);
                const requestsRemaining = Math.max(0, requestsTotal - requestsCompleted);

                setPaginationInfo({
                    currentPage,
                    totalPages: requestsTotal,
                    itemsPerPage,
                    totalItems,
                    actualApiItems,
                    loadedItems: allFeatures.length,
                    requestsCompleted,
                    requestsTotal,
                    requestsRemaining,
                    rejectedItems,
                });

                // Decide to continue
                if (totalItems > 0) {
                    if (currentPage >= requestsTotal) break;
                } else {
                    if (items.length < itemsPerPage || currentPage >= MAP_CONFIG.MAX_PAGES) break;
                }

                currentPage += 1;
            }

            // Fetch metadata from Metabase question 190
            let metabaseMetadata: ANCNextMaintenanceOrEmptying[] = [];
            try {
                metabaseMetadata = await getANCNextMaintenanceOrEmptyingFromQuestion190(token);
            } catch (metabaseError) {
                console.warn('Failed to fetch metadata from Metabase question 190:', metabaseError);
                // Continue without metadata rather than failing completely
            }

            // Merge metadata into features
            const featuresWithMetadata = mergeMetadata(allFeatures, metabaseMetadata);

            // Store all features locally for client-side filtering
            setAllFeatures(featuresWithMetadata);

            // Initially set data to all features (no filters applied yet)
            setData({
                type: 'FeatureCollection',
                features: featuresWithMetadata,
                totalItems,
                page: 1,
                itemsPerPage,
                totalPages: totalItems > 0 ? Math.max(1, Math.ceil(totalItems / itemsPerPage)) : undefined,
            });
            
        } catch (e: any) {
            setError(e?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [transformToFeature, urlBase]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);
    
    // Apply filters client-side whenever filters change
    useEffect(() => {
        if (allFeatures.length > 0) {
            const filteredFeatures = applyFilters(allFeatures, filters);
            setData({
                type: 'FeatureCollection',
                features: filteredFeatures,
                totalItems: filteredFeatures.length,
                page: 1,
                itemsPerPage: filteredFeatures.length,
            });
        }
    }, [filters, allFeatures, applyFilters]);

    return { data, loading, error, paginationInfo, refetch: fetchAllData };
};
